import { languagesIcon, languages, normalizeLanguage } from "components/RegonalSettings/RegionalSettings";
import ThemeSelector from "components/ThemeBoxSelect/ThemeBoxSelect";
import ThemeSwitcher from "components/ThemeSwitcher/ThemeSwitcher";
import { sidebarAccountLinks } from "constants/sidebarLinks/sidebarLinks";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AnyAction } from "redux";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { logout, refresh, removeAccount } from "redux/users/actions";
import { clearSellersData } from "redux/sellers/actions";
import { ReactComponent as AccountIcon } from "assets/icons/new/Profile/Account.svg";
import { ReactComponent as AddIcon } from "assets/icons/new/UI/Add.svg";
import { ReactComponent as ShowListIcon } from "assets/icons/new/UI/ShowList.svg";
import { ReactComponent as LogoutIcon } from "assets/icons/new/Profile/Logout.svg";

const UserDropdown: React.FC<{ sidebarOpen: boolean; hideMdElements: boolean }> = ({ sidebarOpen, hideMdElements }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const menuRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();

    const { currentUser, currentSellerId, savedAccounts, currentCurrency } = useAppSelector(({ users, sellers }) => ({
        currentUser: users.currentUser.data,
        savedAccounts: users.savedAccounts,
        currentSellerId: sellers.currentSeller.info.data?.id,
        currentCurrency: users.currentUser.currency,
    }))

    const normalizedLang = normalizeLanguage(i18n.language);
    const currentLanguageName = languages.find(l => l.language === normalizedLang)?.period || i18n.language;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current && 
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const logoutFromAccount = () => {
        dispatch(logout({ id: currentUser.id }) as unknown as AnyAction)
    }

    const switchAccount = (account: { id: string; refreshToken: string; [key: string]: any }) => {
        if (account.id === currentUser.id) return;
        dispatch(clearSellersData() as unknown as AnyAction);
        dispatch(refresh({ id: account.id, refreshToken: account.refreshToken }) as unknown as AnyAction);
        setIsMenuOpen(false);
    };

    const removeAccountHandler = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        dispatch(removeAccount(id) as unknown as AnyAction);
    };

    return (
        <div className="relative h-full [--strategy:static] md:[--strategy:absolute] [--adaptive:static] md:[--adaptive:absolute]">
            <button
                ref={buttonRef}
                onClick={toggleMenu}
                id="userDropdownButton"
                className={`flex items-center gap-x-3 h-full w-full hover:text-theme-hover dark:hover:text-theme-hover`}
            >
                <div className="shrink-0">
                    {currentUser?.avatar ?
                        <img src={currentUser.avatar} alt="" className='w-5 h-5 min-w-5 rounded-full' /> :
                        <AccountIcon width="20" height="20" fill="currentColor" />
                    }
                </div>
                <div
                    className={`transition-all duration-300 hidden items-center space-x-1 ${!hideMdElements ? 'lg:flex' : ''} ${
                        sidebarOpen ? 'min-w-[150px] opacity-100' : 'ml-0 opacity-0 w-0 hidden'
                    }`}
                >
                    <div className="w-[calc(100%_-_18px)]">
                        <div className='text-[13px] truncate text-start text-ellipsis'>{currentUser.name}</div>
                        <div className='text-[11px] truncate text-start text-ellipsis'>{currentUser.email}</div>
                    </div>
                    <div className={`flex flex-col space-y-[2px]${sidebarOpen ? '' : ' hidden'}`}>
                        <ShowListIcon width="24" height="24" fill="currentColor" className="shrink-0 size-3.5" />
                    </div>
                </div>
            </button>
            
            {isMenuOpen && (
                <div
                    ref={menuRef}
                    className={`transition-[opacity,margin] w-52.5 duration-300 opacity-100 z-20 shadow-2xl border border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg text-[13px] lg:rounded-xl rounded-bl-xl absolute lg:bottom-0 right-0 max-sm:-right-[39px] lg:right-auto ${sidebarOpen ? 'lg:left-[185px]' : 'lg:left-[33px]'} lg:mt-0 mt-[7px]`}
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="avatarBtn"
                >
                    <div>
                        {sidebarAccountLinks.map((item, i) => {
                            const path = item.path.replace(":id", currentSellerId?.toString() || '');
                            return (
                                <Link
                                    key={item.name}
                                    onClick={toggleMenu}
                                    className={`flex items-center gap-x-3 py-2 px-3 text-[13px] ${i === 0 ? 'lg:rounded-t-[10px]' : ''} hover:text-theme-color-active dark:hover:text-theme-color-active hover:bg-theme-hover dark:hover:bg-theme-hover`}
                                    to={(!item.noSellerId && currentSellerId) ? `/${currentSellerId}${path}` : path}
                                >
                                    <item.Icon style={{ width: '20px', height: '20px' }} className="fill-current" />
                                    <span className="overflow-hidden text-ellipsis whitespace-nowrap max-w-36">{t(item.name)}</span>
                                </Link>
                            )
                        })}
                    </div>
                    <div className={`py-2 px-3 hover:text-theme-color-active dark:hover:text-theme-color-active hover:bg-theme-hover dark:hover:bg-theme-hover border-t border-theme-border dark:border-theme-border`}>
                        {/* <div>{t('Team management')}</div> */}
                        {/* <button className="mt-3" onClick={logoutFromAccount}>{t('logout')}</button> */}
                        <button className="flex items-center gap-x-3 " onClick={logoutFromAccount}>
                            <LogoutIcon width="20" height="20" fill="currentColor" />
                            <span className="overflow-hidden text-ellipsis whitespace-nowrap max-w-36">{t('logout')}</span>
                        </button>
                    </div>
                    
                    <div className={`py-2 px-3 border-t border-theme-border dark:border-theme-border`}>
                        <div className={`grid grid-cols-2 items-center gap-2 hover:text-theme-hover dark:hover:text-theme-hover`}>
                            <label className="flex-1 cursor-pointer text-[13px]">{t('Thema')}</label>
                            <ThemeSwitcher />
                        </div>
                        <div className={`grid grid-cols-2 items-center gap-2 mt-3 hover:text-theme-hover dark:hover:text-theme-hover`}>
                            <label className="flex-1 cursor-pointer text-[13px]">{t('Theme box')}</label>
                            <ThemeSelector />
                        </div>
                        <div className={`grid grid-cols-2 items-center gap-2 mt-3 hover:text-theme-hover dark:hover:text-theme-hover`}>
                            <span className="flex-1 text-[13px]">{t('Text')}</span>
                            <div className="flex space-x-2">
                                <button>{t("Text direction L")}</button>
                                <label htmlFor="hs-text-switch" className="relative inline-block w-11 h-6 cursor-pointer">
                                    <input type="checkbox" id="hs-text-switch" className="peer sr-only" />
                                    <span className={`absolute inset-0 bg-theme-bg rounded-full transition-colors duration-200 ease-in-out peer-checked:bg-theme-bg-active dark:bg-theme-bg dark:peer-checked:bg-theme-bg-active`}></span>
                                    <span className={`absolute top-1/2 start-0.5 -translate-y-1/2 size-5 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out peer-checked:translate-x-full dark:bg-neutral-400 dark:peer-checked:bg-white`}></span>
                                </label>
                                <button>{t("Text direction R")}</button>
                            </div>
                        </div>
                        <div className={`grid grid-cols-2 items-center gap-2 mt-3 hover:text-theme-hover dark:hover:text-theme-hover`}>
                            <span className="flex-1 text-[13px]">{t('Currency')}</span>
                            <button
                                type="button"
                                className="flex items-center text-[13px]"
                                data-hs-overlay="#regionalSettingsModal"
                                aria-expanded="false"
                            >
                                {currentCurrency}
                            </button>
                        </div>
                        <div className={`grid grid-cols-2 items-center gap-2 mt-3 hover:text-theme-hover dark:hover:text-theme-hover`}>
                            <span className="flex-1 text-[13px]">{t('regional')}</span>
                            <button
                                type="button"
                                className="flex items-center justify-between w-full text-[13px]"
                                data-hs-overlay="#regionalSettingsModal"
                                aria-expanded="false"
                            >
                                <span className="shrink-0 size-4 rounded-full mr-1" dangerouslySetInnerHTML={{ __html: languagesIcon[normalizedLang] || '' }} />
                                <span className="truncate">{currentLanguageName}</span>
                            </button>
                        </div>
                    </div>
                    
                    <div className={`p-1 border-t border-theme-border dark:border-theme-border`}>
                        <div className="space-y-1">
                            {savedAccounts?.map((account) => {
                                const isActive = currentUser.id === account.id;
                                return (
                                    <div
                                        key={account.id}
                                        onClick={() => switchAccount(account)}
                                        className={`relative p-1 ps-2 group flex flex-wrap justify-between items-center gap-1 rounded-lg ${!isActive ? 'cursor-pointer' : ''} hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active dark:hover:text-theme-color-active`}
                                    >
                                        <div className="grow w-full">
                                            <div className="flex gap-x-2">
                                                <div className="shrink-0">
                                                    <div className="relative shrink-0">
                                                        <span className={`flex shrink-0 justify-center items-center text-theme-color dark:text-theme-color size-5 bg-gray-200 text-[11px] font-semibold rounded-full`}>A</span>
                                                        {isActive && <span className="absolute -bottom-0.5 -end-0.5 flex justify-center items-center size-3 bg-green-600 border border-white text-white rounded-full">
                                                            <svg className="shrink-0 size-2" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                                                        </span>}
                                                    </div>
                                                </div>

                                                <div className="grow truncate pe-6">
                                                    <span className="block text-[13px] truncate">
                                                        {t('User')}
                                                    </span>
                                                    <span className="block text-[11px] truncate">
                                                        {account.email}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        {!isActive && (
                                            <div className="absolute top-1/2 end-2 -translate-y-1/2 flex items-center gap-x-1 lg:opacity-0 lg:group-hover:opacity-100">
                                                <button
                                                    type="button"
                                                    onClick={(e) => removeAccountHandler(e, account.id)}
                                                    className="size-7 shrink-0 inline-flex justify-center items-center gap-x-2 rounded-full disabled:opacity-50 disabled:pointer-events-none"
                                                >
                                                    <span className="sr-only">{t('Remove')}</span>
                                                    <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className={`border-t rounded-bl-lg lg:rounded-b-lg border-theme-border dark:border-theme-border hover:text-theme-hover dark:hover:text-theme-hover`}>
                        <a
                            href={`${process.env.REACT_APP_AUTH_APP_ADD_ACCOUNT_URL}`}
                            className={`flex justify-center p-2 gap-x-3 text-[13px] font-medium rounded-bl-lg lg:rounded-b-lg focus:outline-hidden hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active dark:hover:text-theme-color-active`}
                        >
                            <AddIcon width="20" height="20" fill="currentColor" />
                            <span className="overflow-hidden text-ellipsis whitespace-nowrap max-w-36">{t('Add account')}</span>
                        </a>
                    </div>
                </div>
            )}
        </div>
    )
}

export default UserDropdown;